/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from "react";

import {
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  message,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import fileDownload from "js-file-download";
import moment from "moment";
import PropTypes from "prop-types";

import useDebounce from "../../hooks/useDebounce";
import { getRefernEarnPartnerSales } from "../api";

const formatIndianNumber = (value) => {
  return new Intl.NumberFormat("en-IN").format(value);
};

const PartnersSales = ({ setCurrentScreen, screenData }) => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    status: "", // completed | refunded
    pageSize: 10,
    page: 1,
    search: "",
  });
  const [loading, setLoading] = useState(false);

  const debouncedSearch = useDebounce({
    value: search,
    callback: () => {},
  });
  const [tableKey, setTableKey] = useState(Date.now());

  const handleGetRefernEarnPartnerSales = async (csv = false) => {
    setLoading(true);
    let hide;
    if (csv) {
      hide = message.loading("Generating CSV...", 0);
    }
    try {
      const res = await getRefernEarnPartnerSales(
        {
          ...filters,
          productId: screenData?.product?._id,
        },
        csv
      );

      if (csv) {
        fileDownload(res, "refern_earn_partners_sale.csv");
      } else if (res?.code === 200) {
        setData(res.result.sales.map((item, index) => ({ ...item, index })));
        setTotal(res.result.totalSales);
        setTableKey(Date.now());
      } else {
        message.error("Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      if (hide) hide();
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value) => moment(value).format("DD MMM YYYY"),
    },
    {
      title: "Referred Creator",
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <Space size="small" direction="vertical">
          <Typography style={{}}>{record?.name}</Typography>
          <Typography>{record?.email}</Typography>
          <Typography>{record?.phone}</Typography>
          <Typography>({record?.host})</Typography>
        </Space>
      ),
    },
    {
      title: "TagMango Commission",
      dataIndex: "onetimeCharge",
      key: "onetimeCharge",
      defaultSortOrder: null,
      sortDirections: ["descend", "ascend"],
      sorter: (a, b) => a.onetimeCharge - b.onetimeCharge,
      render: (value) => (
        <Statistic
          value={value}
          precision={0}
          formatter={formatIndianNumber}
          valueStyle={{ fontSize: 16 }}
          prefix={<span className="siteInr">&#8377;</span>}
        />
      ),
    },
    {
      title: "Referrer's Commission",
      dataIndex: "referrersCommissionEarned",
      key: "referrersCommissionEarned",
      defaultSortOrder: null,
      sortDirections: ["descend", "ascend"],
      sorter: (a, b) =>
        a.referrersCommissionEarned - b.referrersCommissionEarned,
      render: (value) => (
        <Statistic
          value={value}
          precision={0}
          formatter={formatIndianNumber}
          valueStyle={{ fontSize: 16 }}
          prefix={<span className="siteInr">&#8377;</span>}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => (
        <Tag
          color={
            value === "completed"
              ? "green"
              : value === "refunded"
              ? "red"
              : "yellow"
          }
          style={{ textTransform: "capitalize" }}
        >
          {value}
        </Tag>
      ),
    },
  ];

  useEffect(() => {
    handleGetRefernEarnPartnerSales();
  }, [filters]);

  useEffect(() => {
    if (debouncedSearch) {
      setFilters((prev) => ({
        ...prev,
        search: debouncedSearch,
        page: 1,
      }));
    }
  }, [debouncedSearch]);

  return (
    <>
      <Space direction="vertical">
        <Typography.Link
          onClick={() => {
            setCurrentScreen({ name: "list", data: null });
          }}
        >
          Back to partners
        </Typography.Link>

        <Typography style={{ fontSize: 24 }}>
          {screenData?.product?.productName} - Sales
        </Typography>

        {screenData?.user ? (
          <Card style={{ marginBottom: "16px" }}>
            <Space size="small" direction="vertical">
              <Typography style={{}}>{screenData?.user?.name}</Typography>
              <Typography>{screenData?.user?.email}</Typography>
              <Typography>{screenData?.user?.phone}</Typography>
              <Typography>({screenData?.user?.host})</Typography>
            </Space>
          </Card>
        ) : null}
      </Space>

      <Row gutter={6} style={{ marginTop: "24px" }}>
        <Col span={12}>
          <Input
            minLength={2}
            className="searchbar"
            value={search}
            style={{ width: "100%" }}
            placeholder="Search by partner name, email or mobile"
            onChange={(event) => {
              if (event.target.value.length === 0) {
                setFilters({
                  ...filters,
                  search: "",
                });
              }
              setSearch(event.target.value);
            }}
          />
        </Col>
        <Col span={12}>
          <Space>
            <DatePicker.RangePicker
              value={[filters.startDate, filters.endDate]}
              onChange={(dates) => {
                setFilters({
                  ...filters,
                  startDate: dates?.[0].startOf("day"),
                  endDate: dates?.[1].endOf("day"),
                });
              }}
              allowClear
            />
            <Button
              type="primary"
              onClick={() => {
                handleGetRefernEarnPartnerSales(true);
              }}
            >
              Export CSV
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        key={tableKey}
        columns={columns}
        dataSource={data}
        loading={loading}
        style={{
          marginTop: "20px",
        }}
        pagination={{
          current: filters.page,
          total,
          pageSize: filters.pageSize,
          onChange: (p) => setFilters({ ...filters, page: p }),
        }}
        rowKey={(record) => record._id}
      />
    </>
  );
};

PartnersSales.propTypes = {
  setCurrentScreen: PropTypes.func.isRequired,
  screenData: PropTypes.shape({
    product: PropTypes.shape(),
    user: PropTypes.shape(),
  }),
};
export default PartnersSales;
